import { useState } from 'react';
import { useCases, type CaseItem } from '../features/cases/store';
import { Icon } from '../components/Icon';

const DIFF = ['', 'EASY', 'MEDIUM', 'HARD'] as const;
const diffClass = (d: number) => (d === 3 ? '--red' : d === 2 ? '--orange' : '--green');

function CaseCard({ c, open }: { c: CaseItem; open: () => void }) {
  return (
    <button className={`case-card${c.status === 'solved' ? ' case-card--solved' : ''}`} onClick={open}>
      <div className="case-card__top">
        <span className={`event-type event-type${diffClass(c.difficulty)}`}>{DIFF[c.difficulty]}</span>
        <span className="mono dim">{c.points} PTS</span>
      </div>
      <h3>{c.title}</h3>
      {c.image && (
        <div className="case-card__img">
          <img src={c.image} alt={c.title} loading="lazy" />
        </div>
      )}
      <p className="dim">{c.brief.slice(0, 140)}…</p>
      <div className="case-card__foot">
        <span className={`live-dot${c.status === 'open' ? ' live-dot--on' : ''}`} />
        <span className="mono">{c.status === 'open' ? 'OPEN' : `SOLVED · ${c.solver}`}</span>
        <span className="mono dim">{c.submissions.length} submissions</span>
      </div>
    </button>
  );
}

export function CasesPage() {
  const { cases, leaderboard, submit, accept } = useCases();
  const [openId, setOpenId] = useState<string | null>(null);
  const [author, setAuthor] = useState('');
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [justAdded, setJustAdded] = useState('');
  const active = cases.find((c) => c.id === openId) ?? null;

  const close = () => {
    setOpenId(null);
    setText('');
    setLink('');
    setJustAdded('');
  };

  const doSubmit = (c: CaseItem) => {
    if (!text.trim()) return;
    const sub = submit(c.id, author, text, link);
    setJustAdded(sub.id);
    setText('');
    setLink('');
  };

  const open = cases.filter((c) => c.status === 'open');
  const solved = cases.filter((c) => c.status === 'solved');

  return (
    <div className="page" data-testid="cases-page">
      <section className="page-intro">
        <div>
          <span className="eyebrow">Challenge arena</span>
          <h1>Cases</h1>
          <p>
            Real OSINT challenges. Solve them with the workbench — first verified solution takes the points.
          </p>
        </div>
      </section>

      {leaderboard.length > 0 && (
        <section className="panel leaderboard">
          <div className="panel__head">
            <span className="eyebrow">SCOREBOARD</span>
          </div>
          <div className="lb-row">
            {leaderboard.slice(0, 5).map(([name, pts], i) => (
              <span key={name} className="lb-entry">
                <b>#{i + 1}</b> {name} <span className="mono dim">{pts} PTS</span>
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="case-grid">
        {open.map((c) => (
          <CaseCard key={c.id} c={c} open={() => setOpenId(c.id)} />
        ))}
        {solved.map((c) => (
          <CaseCard key={c.id} c={c} open={() => setOpenId(c.id)} />
        ))}
      </section>

      {active && (
        <div className="sheet-backdrop" onClick={close}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sheet__head">
              <div>
                <span className={`event-type event-type${diffClass(active.difficulty)}`}>{DIFF[active.difficulty]}</span>
                <span className="mono dim"> {active.points} PTS</span>
              </div>
              <button className="chip" onClick={close}>
                ×
              </button>
            </div>
            <h2>{active.title}</h2>
            {active.image && (
              <figure className="case-figure">
                <img src={active.image} alt={active.title} />
                {active.credit && <figcaption>{active.credit}</figcaption>}
              </figure>
            )}
            <p>{active.brief}</p>

            <details className="hints">
              <summary>Hinweise ({active.hints.length})</summary>
              <ul>
                {active.hints.map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>
            </details>

            {active.status === 'solved' && (
              <div className="solved-banner">
                <Icon name="check" size={16} /> SOLVED by <b>{active.solver}</b> · {active.solvedAt?.slice(0, 10)}
              </div>
            )}

            <div className="panel panel--inset">
              <div className="panel__head">
                <span className="eyebrow">SUBMISSIONS ({active.submissions.length})</span>
              </div>
              {active.submissions.length === 0 && <div className="empty-state">No submissions yet — be first.</div>}
              {active.submissions.map((s) => (
                <div key={s.id} className={`sub${s.accepted ? ' sub--accepted' : ''}`}>
                  <div className="sub__head">
                    <span className="mono">{s.author}</span>
                    <span className="mono dim">{new Date(s.at).toLocaleString('de-DE')}</span>
                    {s.accepted && (
                      <span className="event-type event-type--green">
                        <Icon name="check" size={12} /> ACCEPTED
                      </span>
                    )}
                  </div>
                  <p>{s.text}</p>
                  {s.link && (
                    <a href={s.link} target="_blank" rel="noopener noreferrer" className="sub__link">
                      <Icon name="link" size={13} /> {s.link}
                    </a>
                  )}
                  {active.status === 'open' && (
                    <button
                      className="chip chip--on"
                      onClick={() => accept(active.id, s.id)}
                      disabled={justAdded === s.id}
                    >
                      {justAdded === s.id ? 'Submitted — waiting for review' : 'Mark as correct'}
                    </button>
                  )}
                </div>
              ))}
            </div>

            {active.status === 'open' && (
              <div className="panel panel--inset">
                <div className="panel__head">
                  <span className="eyebrow">SUBMIT SOLUTION</span>
                </div>
                <label className="field">
                  <span className="mono dim">CODENAME</span>
                  <input value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="OSPREY" />
                </label>
                <label className="field">
                  <span className="mono dim">ANSWER</span>
                  <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Standort / Einheit / Zeit — mit Begründung" />
                </label>
                <label className="field">
                  <span className="mono dim">EVIDENCE LINK</span>
                  <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://… (optional)" />
                </label>
                <button className="btn" onClick={() => doSubmit(active)} disabled={!text.trim()}>
                  <Icon name="target" size={14} /> Submit solution
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
